/**
 * THE OFFLINE OUTBOX — the heart of the app.
 * =========================================
 *
 * PRD §4.3: *"a technician on flaky site wifi must be able to get a 202 and walk away."* The
 * incumbent path loses **26.9% of rows**. This module is the client half of not losing them: a
 * submission enters IndexedDB before any network call is attempted, and it stays there until the
 * server has acknowledged it or a human has been told it failed.
 *
 * WHAT "DURABLE" MEANS HERE, PRECISELY. `enqueue` resolves only after the IndexedDB **transaction
 * commits** (see `db.ts` — resolving on the request's `success` event instead would report work as
 * saved that a later abort discards). So by the time the caller's `await` returns, the item survives:
 * navigating away, the tab being killed, iOS suspending the app, a device reboot, and being offline
 * for days. The one thing it does not survive is the browser EVICTING the origin's storage, which is
 * a real hazard on iOS and is why `storage.ts` requests persistence and the UI reports the answer
 * honestly instead of assuming it.
 *
 * THE STATE MACHINE. Four states, and the transitions are the whole contract:
 *
 *     enqueue ──► queued ──► in-flight ──► synced        (terminal, success)
 *                   ▲            │
 *                   └────────────┤ retryable failure: attempts+1, nextAttemptAt = now + backoff
 *                                │
 *                                └──────► failed         (terminal, needs a human)
 *
 * · `queued`    — durable, waiting. `nextAttemptAt` absent means "due now".
 * · `in-flight` — a request is in the air. **Not durable state, and treated as suspect on load:**
 *                 an `in-flight` row found at startup means the app died mid-request, so
 *                 `recoverInFlight()` returns it to `queued`. Leaving it would strand the item
 *                 forever, which is the silent-loss failure this file exists to prevent. The retry
 *                 is safe precisely because the idempotency key is stable — a request that DID land
 *                 before we died resolves to the same server row rather than a duplicate.
 * · `synced`    — the server accepted it (202). Terminal.
 * · `failed`    — terminal, and it means *a human must look*. Reached by a permanent rejection (a
 *                 4xx that is not 408/429) or by exhausting `MAX_ATTEMPTS`. Never reached by being
 *                 offline.
 *
 * THE IDEMPOTENCY KEY IS MINTED **ONCE, HERE, AT ENQUEUE**, and is a stored field of the row. There
 * is no code path that recomputes it, and `sync.ts` reads `item.idempotencyKey` rather than
 * generating one — so every retry of an item carries the same key and the server's C-18 namespacing
 * collapses them onto one `Work_Submission__c` row. Regenerating per attempt would make every retry
 * a new submission on an object that feeds QuickBooks vendor billing through nine consumers.
 *
 * ORDERING. `getAll()` returns key order, and the row id is `<timestamp>-<sequence>-<random>` (see
 * `ids.ts`), so the queue drains oldest-first: the submission the technician finished first is the one
 * that lands first, which is what they will expect when they read the list back. Ordering is a UX
 * property, not a correctness one — the flusher attempts every due item regardless — and `ids.ts`
 * records the one edge where it is not guaranteed.
 */

import { OUTBOX_STORE, request, withTransaction } from './db';
import { newIdempotencyKey } from './idempotency';
import { newOutboxId } from './ids';
import { attemptsExhausted, computeBackoffDelay } from './backoff';

export type OutboxState = 'queued' | 'in-flight' | 'synced' | 'failed';

export interface OutboxItem<T = unknown> {
  id: string;
  idempotencyKey: string;
  kind: string;
  payload: T;
  state: OutboxState;
  attempts: number;
  createdAt: number;
  lastError?: string;
  nextAttemptAt?: number;
}

type Listener = (items: OutboxItem[]) => void;

const listeners = new Set<Listener>();

/**
 * Cross-tab notification, so a second tab's flush updates this tab's badge.
 *
 * Best-effort and guarded: `BroadcastChannel` is absent in older WebKit, and a missing channel must
 * degrade to "this tab shows its own changes" rather than throwing on module load. It carries no
 * payload — a signal to re-read IndexedDB, never data, because IndexedDB is the single source of
 * truth and a message body would be a second one that can disagree.
 */
const channel: BroadcastChannel | null = (() => {
  if (typeof BroadcastChannel === 'undefined') {
    return null;
  }
  try {
    const created = new BroadcastChannel('lockercustody-outbox');
    created.onmessage = () => {
      void notify();
    };
    return created;
  } catch {
    return null;
  }
})();

async function notify(broadcast = false): Promise<void> {
  if (broadcast && channel !== null) {
    try {
      channel.postMessage('changed');
    } catch {
      // A closed channel must not break a state transition that already committed.
    }
  }
  if (listeners.size === 0) {
    return;
  }
  const items = await getAll();
  for (const listener of listeners) {
    try {
      listener([...items]);
    } catch {
      // A throwing subscriber (a React render error, say) must not abort the flush loop or
      // prevent other subscribers from seeing the change.
    }
  }
}

/**
 * Durably enqueue an item. Resolves once it is committed to disk.
 *
 * The returned item is the stored row, including its `idempotencyKey` — the caller may want it for
 * display or correlation, but must never mint its own.
 */
export async function enqueue<T>(kind: string, payload: T): Promise<OutboxItem<T>> {
  const item: OutboxItem<T> = {
    id: newOutboxId(),
    idempotencyKey: newIdempotencyKey(),
    kind,
    payload,
    state: 'queued',
    attempts: 0,
    createdAt: Date.now(),
  };
  await withTransaction(OUTBOX_STORE, 'readwrite', (tx) => {
    // `add`, not `put`: a colliding id is a generator bug and must be loud, not an overwrite of
    // somebody's queued submission.
    tx.objectStore(OUTBOX_STORE).add(item);
  });
  await notify(true);
  return item;
}

/**
 * Subscribe to the whole queue. Called immediately with the current contents, then on every change.
 *
 * Whole-list rather than per-item events: the UI renders counts and a list, and a diff protocol
 * would be a second model of the queue that can drift from IndexedDB.
 */
export function subscribe(fn: (items: OutboxItem[]) => void): () => void {
  listeners.add(fn);
  void getAll()
    .then((items) => fn([...items]))
    .catch(() => fn([]));
  return () => {
    listeners.delete(fn);
  };
}

/** Every row, in creation order. */
export async function getAll(): Promise<readonly OutboxItem[]> {
  return withTransaction(OUTBOX_STORE, 'readonly', (tx) =>
    request<OutboxItem[]>(tx.objectStore(OUTBOX_STORE).getAll() as IDBRequest<OutboxItem[]>),
  );
}

export async function get(id: string): Promise<OutboxItem | undefined> {
  return withTransaction(OUTBOX_STORE, 'readonly', (tx) =>
    request<OutboxItem | undefined>(
      tx.objectStore(OUTBOX_STORE).get(id) as IDBRequest<OutboxItem | undefined>,
    ),
  );
}

/**
 * The items the flusher should attempt now: `queued`, and either unscheduled or past due.
 *
 * Read-and-filter rather than an index range query. The `byNextAttemptAt` index cannot express this
 * in one cursor, because an item with NO `nextAttemptAt` is absent from that index entirely (that is
 * how IndexedDB treats a missing key path) while being exactly the case we most want to return — a
 * freshly enqueued item. A range query would silently skip every first attempt, which is a bug that
 * only shows up as "the queue never drains until something fails".
 */
export async function dueItems(now = Date.now()): Promise<readonly OutboxItem[]> {
  const all = await getAll();
  return all.filter(
    (item) => item.state === 'queued' && (item.nextAttemptAt === undefined || item.nextAttemptAt <= now),
  );
}

/** Read-modify-write inside ONE transaction, so two concurrent transitions cannot interleave. */
async function mutate(
  id: string,
  change: (item: OutboxItem) => OutboxItem | null,
): Promise<OutboxItem | null> {
  const result = await withTransaction(OUTBOX_STORE, 'readwrite', async (tx) => {
    const store = tx.objectStore(OUTBOX_STORE);
    const current = await request<OutboxItem | undefined>(
      store.get(id) as IDBRequest<OutboxItem | undefined>,
    );
    if (current === undefined) {
      return null;
    }
    const next = change(current);
    if (next === null) {
      return null;
    }
    store.put(next);
    return next;
  });
  if (result !== null) {
    await notify(true);
  }
  return result;
}

/**
 * Claim an item for sending. Returns `null` if it is not `queued` any more.
 *
 * The claim is a real one: because the read and the write are in a single `readwrite` transaction,
 * two flushers (this tab and another tab, or a `visibilitychange` racing a `online` event) cannot
 * both claim the same row and send it twice.
 */
export async function markInFlight(id: string): Promise<OutboxItem | null> {
  return mutate(id, (item) => {
    if (item.state !== 'queued') {
      return null;
    }
    // `attempts` is incremented HERE, when the attempt begins, not on failure. If the app dies
    // mid-request the attempt still counted, so a crash loop cannot retry without bound.
    const next: OutboxItem = { ...item, state: 'in-flight', attempts: item.attempts + 1 };
    delete next.nextAttemptAt;
    return next;
  });
}

export async function markSynced(id: string): Promise<OutboxItem | null> {
  return mutate(id, (item) => {
    const next: OutboxItem = { ...item, state: 'synced' };
    delete next.nextAttemptAt;
    delete next.lastError;
    return next;
  });
}

/** Terminal failure. `reason` is shown to the technician, so it must be a sentence, not a stack. */
export async function markFailed(id: string, reason: string): Promise<OutboxItem | null> {
  return mutate(id, (item) => {
    const next: OutboxItem = { ...item, state: 'failed', lastError: reason };
    delete next.nextAttemptAt;
    return next;
  });
}

/**
 * Retryable failure: back to `queued` with a jittered backoff — or to `failed` if the attempt
 * budget is spent.
 *
 * `rng` is injectable so the schedule can be asserted rather than mocked.
 */
export async function scheduleRetry(
  id: string,
  reason: string,
  now = Date.now(),
  rng: () => number = Math.random,
): Promise<OutboxItem | null> {
  return mutate(id, (item) => {
    if (attemptsExhausted(item.attempts)) {
      // Exhausted, not permanent — but the outcome is the same and the message says which, because
      // "we gave up after 12 tries" and "the server rejected this" need different human responses.
      return { ...item, state: 'failed', lastError: `gave up after ${item.attempts} attempts: ${reason}` };
    }
    return {
      ...item,
      state: 'queued',
      lastError: reason,
      nextAttemptAt: now + computeBackoffDelay(item.attempts, rng),
    };
  });
}

/**
 * Give a claim back WITHOUT consuming the attempt.
 *
 * For failures that are not the item's fault and not a transport problem either — today that means
 * "the session went away mid-flight". Burning one of twelve attempts because a token refresh failed
 * would let a sign-in problem quietly exhaust a queue of real work and mark it `failed`.
 */
export async function releaseClaim(id: string, reason: string): Promise<OutboxItem | null> {
  return mutate(id, (item) => {
    if (item.state !== 'in-flight') {
      return null;
    }
    const next: OutboxItem = {
      ...item,
      state: 'queued',
      attempts: Math.max(0, item.attempts - 1),
      lastError: reason,
    };
    delete next.nextAttemptAt;
    return next;
  });
}

/**
 * Return every `in-flight` row to `queued`. **Call this on startup, before the first flush.**
 *
 * An `in-flight` row at startup means the process died between sending and recording the outcome.
 * The request may or may not have landed, and we cannot know which — but retrying is safe because
 * the stored idempotency key is stable, so a request that DID land dedupes server-side. The
 * alternative (leaving it in-flight) strands the item forever with no UI affordance to fix it.
 */
export async function recoverInFlight(): Promise<number> {
  const all = await getAll();
  const stranded = all.filter((item) => item.state === 'in-flight');
  if (stranded.length === 0) {
    return 0;
  }
  await withTransaction(OUTBOX_STORE, 'readwrite', (tx) => {
    const store = tx.objectStore(OUTBOX_STORE);
    for (const item of stranded) {
      const recovered: OutboxItem = {
        ...item,
        state: 'queued',
        lastError: 'interrupted before the server answered; will retry',
      };
      // Due immediately: the app has just started, which usually means the technician is looking at
      // it, and a stranded item is the one they most want to see move. Deleted rather than set to
      // `undefined` so the `byNextAttemptAt` index has no entry at all for it.
      delete recovered.nextAttemptAt;
      store.put(recovered);
    }
  });
  await notify(true);
  return stranded.length;
}

/** Removes a terminal row. Only `synced` or `failed` rows may be removed. */
export async function remove(id: string): Promise<boolean> {
  const removed = await withTransaction(OUTBOX_STORE, 'readwrite', async (tx) => {
    const store = tx.objectStore(OUTBOX_STORE);
    const current = await request<OutboxItem | undefined>(
      store.get(id) as IDBRequest<OutboxItem | undefined>,
    );
    if (current === undefined || current.state === 'queued' || current.state === 'in-flight') {
      // Refusing to delete live work is the point. A "clear queue" button that can drop a queued
      // submission is a data-loss button with a tidy label.
      return false;
    }
    store.delete(id);
    return true;
  });
  if (removed) {
    await notify(true);
  }
  return removed;
}

/**
 * Put a `failed` row back in the queue at attempt zero. The technician's "try again" button.
 *
 * The idempotency key is deliberately NOT regenerated, so a retry after a fix still lands on the
 * same server row. That is the whole reason the key is stored rather than derived.
 */
export async function retryFailed(id: string): Promise<OutboxItem | null> {
  return mutate(id, (item) => {
    if (item.state !== 'failed') {
      return null;
    }
    const next: OutboxItem = { ...item, state: 'queued', attempts: 0 };
    delete next.nextAttemptAt;
    return next;
  });
}

export interface OutboxCounts {
  readonly queued: number;
  readonly inFlight: number;
  readonly synced: number;
  readonly failed: number;
}

export function countByState(items: readonly OutboxItem[]): OutboxCounts {
  return {
    queued: items.filter((item) => item.state === 'queued').length,
    inFlight: items.filter((item) => item.state === 'in-flight').length,
    synced: items.filter((item) => item.state === 'synced').length,
    failed: items.filter((item) => item.state === 'failed').length,
  };
}
